import { Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { StaffSiteSale, StaffSiteSaleLineItem } from '../../models/staff-site-sale';
import { LightspeedRegistersForSaleResponse } from '../../models/lightspeed-register';
import { propExists } from '../../utils';
import { LIVE_READER_PROMPT_SUPPORTED_FIELD } from '../payment/payment.service';


export interface ApplyDiscountCodeToLineItemResponse {
  text: string;
  line_item_id: number;
  sale: StaffSiteSale;
}


@Injectable({
  providedIn: 'root'
})
export class StaffSiteSaleService {

  constructor(
    private apiService: ApiService
  ) {}

  createStaffSiteSale(customerId: number): Promise<StaffSiteSale> {
    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/',
      {customer_id: customerId}
    );
  }

  getStaffSiteSale(saleId: number): Promise<StaffSiteSale> {
    return this.apiService.getItem<StaffSiteSale>(
      'staff-site-sales/',
      saleId
    );
  }

  updateStaffSiteSale(saleId: number, customerId: number): Promise<StaffSiteSale> {
    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/' + saleId.toString() + '/',
      {customer_id: customerId}
    );
  }

  cancelStaffSiteSale(saleId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'staff-site-sales/' + saleId.toString() + '/cancel/',
      {}
    ).then();
  }

  createLineItem(saleId: number, lineItem: StaffSiteSaleLineItem): Promise<StaffSiteSale> {
    const requestBody: any = {
      purchase_type: lineItem.purchase_type,
      product_type_id: lineItem.product_type_id,
      customer_id: lineItem.customer_id,
      add_on_ids: lineItem.add_on_ids,
    }

    if (propExists(lineItem.product_location_id)) {
      requestBody.product_location_id = lineItem.product_location_id;
    }

    if (propExists(lineItem.product_date_range)) {
      requestBody.product_date_range = lineItem.product_date_range;
    } else if (propExists(lineItem.product_start_date)) {
      requestBody.product_start_date = lineItem.product_start_date;
    }

    if (propExists(lineItem.order_number)) {
      requestBody.order_number = lineItem.order_number;
    }

    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/' + saleId.toString() + '/add-line-item/',
      requestBody,
    );
  }

  removeLineItem(saleId: number, lineItemId: number): Promise<StaffSiteSale> {
    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/' + saleId.toString() + '/remove-line-item/' + lineItemId.toString() + '/',
      {}
    );
  }

  applyDiscountCodeToLineItem(saleId: number, lineItemId: number, discountCode: string): Promise<ApplyDiscountCodeToLineItemResponse> {
    return this.apiService.postToAPI<ApplyDiscountCodeToLineItemResponse>(
      'staff-site-sales/' + saleId.toString() + '/apply-discount-code/' + lineItemId.toString() + '/',
      {discount_code: discountCode}
    );
  }

  sendStaffSiteSaleToPos(saleId: number,
                         registerId?: string,
                         bypassLiveReaderCheck?: boolean): Promise<StaffSiteSale> {
    const requestBody: any = {};
    if (propExists(registerId) && registerId !== '') {
      requestBody.register_id = registerId;
    }
    requestBody[LIVE_READER_PROMPT_SUPPORTED_FIELD] = true;
    if (bypassLiveReaderCheck) {
      requestBody.bypass_live_reader_check = true;
    }
    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/' + saleId.toString() + '/send-to-pos/',
      requestBody
    );
  }

  getLightspeedRegistersForSale(saleId: number): Promise<LightspeedRegistersForSaleResponse> {
    return this.apiService.getItem<LightspeedRegistersForSaleResponse>(
      'staff-site-sales/' + saleId.toString() + '/',
      'lightspeed-registers'
    );
  }

  cancelPosSale(saleId: number): Promise<StaffSiteSale> {
    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/' + saleId.toString() + '/cancel-pos/',
      {}
    );
  }

  discardPendingPayment(saleId: number): Promise<StaffSiteSale> {
    return this.apiService.postToAPI<StaffSiteSale>(
      'staff-site-sales/' + saleId.toString() + '/discard-pending-payment/',
      {}
    );
  }

}
